import { Dispatch } from 'react'

export enum GridColumnSortControls {
  DEPARTMENT = 'department',
  ID = 'id',
  NAME = 'name',
  ASSIGNEE = 'assignee',
  POLICY_MATCH = 'policy_match',
  COMPLETENESS = 'completeness',
  SPOT_CHECK = 'spot_check',
  LINKED_EVIDENCE = 'linked_evidence',
}

export enum GridColumnSortPolicies {
  DEPARTMENT = 'department',
  NAME = 'name',
  CONTROLS = 'controls',
  DOCUMENTATION = 'documentation',
  ASSIGNEE = 'assignee',
}

export enum GridColumnSortRequests {
  EVIDENCE = 'evidence',
  CONTROLS = 'controls',
}

export enum GridColumnSortVendors {
  NAME = 'name',
  RISK_TIER = 'risk_tier',
  ATTESTATION = 'attestation',
  RISKS_COUNT = 'risks_count',
}

export enum GridColumnSortEvidence {
  NAME = 'name',
  RELEVANT_DATE = 'relevant_date',
  CREATED_AT = 'created_at',
  LAST_CHECKED = 'last_checked',
}

export enum GridColumnSortDocuments {
  MIMETYPE = 'mimetype',
  NAME = 'name',
  CREATED_AT = 'created_at',
  PRIVACY = 'privacy',
  ACTION = 'action',
  CATEGORY = 'category',
}

export enum GridColumnSortVisitorAnalytics {
  NAME = 'name',
  EMAIL = 'email',
  COMPANY = 'company',
  FEATURES = 'features',
  DOCUMENT_NAME = 'document_name',
  ACCESS_LEVEL = 'access_level',
  FAQ_TITLE = 'faq_title',
  SEARCH_QUERY = 'search_query',
  QUESTION = 'question',
  DATE = 'date',
  CATEGORY = 'category',
}

export enum GridColumnSortRisks {
  ID = 'id',
  Threat = 'name',
  Status = 'status',
  Assignee = 'owner_email',
  'Target residual risk' = 'risk_after_treatment',
  Category = 'computed_category',
  Controls = 'computed_controls',
  'Inherent risk' = 'computed_inherent_risk',
  'Current residual risk' = 'computed_current_residual_risk',
}

export enum GridColumnSortRiskControls {
  DEPT = 'dept',
  ID = 'id',
  CONTROL_EFFECTIVENESS = 'control_effectiveness',
  THREAT = 'threat',
}

export enum GridColumnSortAccessRequests {
  NAME = 'name',
  EMAIL = 'email',
  ORG_NAME = 'org_name',
  STATUS = 'status',
  DATE_REQUESTED = 'date_requested',
  APPROVE_ACTION = 'approve_action',
  DENY_ACTION = 'deny_action',
}

export enum GridColumnSortAccessPermissions {
  NAME = 'name',
  EMAIL = 'email',
  ORG_NAME = 'org_name',
  STATUS = 'status',
  LAST_UPDATED = 'last_updated',
  NDA_SIGNED_AT = 'nda_signed_at',
  TOTAL_TI_CREDITS = 'total_ti_credits',
  ACTION = 'action',
}

export enum GridColumnSortControlsEvidenceMapping {
  DEPARTMENT = 'department',
  ID = 'id',
  NAME = 'name',
  GAPS_IDENTIFIED = 'gaps_identified',
  LINKED_EVIDENCE = 'linked_evidence',
  SUGGESTED_EVIDENCE = 'suggested_evidence',
}

export enum GridColumnSortEvidenceControlMapping {
  EVIDENCE = 'evidence',
  LINKED_CONTROLS = 'linked_controls',
  SUGGESTED_CONTROLS = 'suggested_controls',
}

export enum GridColumnSortType {
  ASCENDING = 'asc',
  DESCENDING = 'desc',
}

export enum GridColumnSortParams {
  SORT_BY = 'sort_by',
  SORT_COL = 'sort_col',
}

export interface GridColumnSortActionControls {
  type: GridColumnSortType
  colName: GridColumnSortControls
}

export interface GridColumnSortActionPolicies {
  type: GridColumnSortType
  colName: GridColumnSortPolicies
}

export interface GridColumnSortActionRequests {
  type: GridColumnSortType
  colName: GridColumnSortRequests
}

export interface GridColumnSortActionVendors {
  type: GridColumnSortType
  colName: GridColumnSortVendors
}

export interface GridColumnSortActionEvidence {
  type: GridColumnSortType
  colName: GridColumnSortEvidence
}

export interface GridColumnSortActionControlsEvidenceMapping {
  type: GridColumnSortType
  colName: GridColumnSortControlsEvidenceMapping
}

export interface GridColumnSortActionEvidenceControlMapping {
  type: GridColumnSortType
  colName: GridColumnSortEvidenceControlMapping
}

export interface GridColumnSortActionRisks {
  type: GridColumnSortType
  colName: GridColumnSortRisks
}

export interface GridColumnSortActionRiskControls {
  type: GridColumnSortType
  colName: GridColumnSortRiskControls
}

export type GridColumnName =
  | GridColumnSortControls
  | GridColumnSortPolicies
  | GridColumnSortRequests
  | GridColumnSortVendors
  | GridColumnSortEvidence
  | GridColumnSortDocuments
  | GridColumnSortAccessRequests
  | GridColumnSortAccessPermissions
  | GridColumnSortRisks
  | GridColumnSortRiskControls
  | GridColumnSortVisitorAnalytics
  | GridColumnSortControlsEvidenceMapping
  | GridColumnSortEvidenceControlMapping

export interface GridColumnSortProps {
  colName: GridColumnName
  showSpinner: boolean
  currIcon: JSX.Element
  applySorting: (e: React.MouseEvent) => void
}

export interface GridColumnSortControlsProps
  extends GridColumnSortActionControls {
  isActive: boolean
  onSort: Dispatch<GridColumnSortActionControls>
}

export interface GridColumnSortPoliciesProps
  extends GridColumnSortActionPolicies {
  isActive: boolean
  onSort: Dispatch<GridColumnSortActionPolicies>
}

export interface GridColumnSortRequestsProps
  extends GridColumnSortActionRequests {
  isActive: boolean
  onSort: Dispatch<GridColumnSortActionRequests>
}

export interface GridColumnSortVendorsProps
  extends GridColumnSortActionVendors {
  isActive: boolean
  onSort: Dispatch<GridColumnSortActionVendors>
}
export interface GridColumnSortEvidenceProps
  extends GridColumnSortActionEvidence {
  isActive: boolean
  onSort: Dispatch<GridColumnSortActionEvidence>
}

export interface GridColumnSortState {
  type: GridColumnSortType
  icon: JSX.Element
}

interface GridColumnSortParamConfig {
  type: GridColumnSortType
  isActive: boolean
}

export type GridColumnSortConfigControls = {
  [key in GridColumnSortControls]: GridColumnSortParamConfig
}

export type GridColumnSortConfigPolicies = {
  [key in GridColumnSortPolicies]: GridColumnSortParamConfig
}
export type GridColumnSortConfigRequests = {
  [key in GridColumnSortRequests]: GridColumnSortParamConfig
}

export type GridColumnSortConfigVendors = {
  [key in GridColumnSortVendors]: GridColumnSortParamConfig
}

export type GridColumnSortConfigEvidence = {
  [key in GridColumnSortEvidence]: GridColumnSortParamConfig
}

export type GridColumnSortConfigControlsEvidenceMapping = {
  [key in GridColumnSortControlsEvidenceMapping]: GridColumnSortParamConfig
}

export type GridColumnSortConfigEvidenceControlMapping = {
  [key in GridColumnSortEvidenceControlMapping]: GridColumnSortParamConfig
}

// Generate the shared reducer state for the column header configs
export const GRID_COL_SORT_CONFIGS_CONTROLS = (
  sortBy?: GridColumnSortType,
  sortCol?: GridColumnSortControls,
): GridColumnSortConfigControls => {
  return Object.values(GridColumnSortControls).reduce((acc, ctrlColName) => {
    acc[ctrlColName] = {
      type: sortBy || GridColumnSortType.DESCENDING,
      isActive: sortCol === ctrlColName,
    }
    return acc
  }, {} as GridColumnSortConfigControls)
}

export const GRID_COL_SORT_CONFIGS_POLICIES = (
  sortBy?: GridColumnSortType,
  sortCol?: GridColumnSortPolicies,
): GridColumnSortConfigPolicies => {
  return Object.values(GridColumnSortPolicies).reduce((acc, ctrlColName) => {
    acc[ctrlColName] = {
      type: sortBy || GridColumnSortType.DESCENDING,
      isActive: sortCol === ctrlColName,
    }
    return acc
  }, {} as GridColumnSortConfigPolicies)
}

export const GRID_COL_SORT_CONFIGS_REQUESTS = (
  sortBy?: GridColumnSortType,
  sortCol?: GridColumnSortRequests,
): GridColumnSortConfigRequests => {
  return Object.values(GridColumnSortRequests).reduce((acc, reqColName) => {
    acc[reqColName] = {
      type: sortBy || GridColumnSortType.DESCENDING,
      isActive: sortCol === reqColName,
    }
    return acc
  }, {} as GridColumnSortConfigRequests)
}

export const GRID_COL_SORT_CONFIGS_VENDORS = (
  sortBy?: GridColumnSortType,
  sortCol?: GridColumnSortVendors,
): GridColumnSortConfigVendors => {
  return Object.values(GridColumnSortVendors).reduce((acc, colName) => {
    acc[colName] = {
      type: sortBy || GridColumnSortType.DESCENDING,
      isActive: sortCol === colName,
    }
    return acc
  }, {} as GridColumnSortConfigVendors)
}

export const GRID_COL_SORT_CONFIGS_EVIDENCE = (
  sortBy?: GridColumnSortType,
  sortCol?: GridColumnSortEvidence,
): GridColumnSortConfigEvidence => {
  return Object.values(GridColumnSortEvidence).reduce((acc, colName) => {
    acc[colName] = {
      type: sortBy || GridColumnSortType.DESCENDING,
      isActive: sortCol === colName,
    }
    return acc
  }, {} as GridColumnSortConfigEvidence)
}

export const GRID_COL_SORT_CONFIGS_CONTROLS_EVIDENCE_MAPPING = (
  sortBy?: GridColumnSortType,
  sortCol?: GridColumnSortControlsEvidenceMapping,
): GridColumnSortConfigControlsEvidenceMapping => {
  return Object.values(GridColumnSortControlsEvidenceMapping).reduce(
    (acc, colName) => {
      acc[colName] = {
        type: sortBy || GridColumnSortType.ASCENDING,
        isActive: sortCol === colName,
      }
      return acc
    },
    {} as GridColumnSortConfigControlsEvidenceMapping,
  )
}

export const GRID_COL_SORT_CONFIGS_EVIDENCE_CONTROL_MAPPING = (
  sortBy?: GridColumnSortType,
  sortCol?: GridColumnSortEvidenceControlMapping,
): GridColumnSortConfigEvidenceControlMapping => {
  return Object.values(GridColumnSortEvidenceControlMapping).reduce(
    (acc, colName) => {
      acc[colName] = {
        type: sortBy || GridColumnSortType.ASCENDING,
        isActive: sortCol === colName,
      }
      return acc
    },
    {} as GridColumnSortConfigEvidenceControlMapping,
  )
}

export const SORT_ORDER = {
  [GridColumnSortType.ASCENDING]: 0,
  [GridColumnSortType.DESCENDING]: 1,
}
Object.freeze(SORT_ORDER)

export enum ControlsSortPrecedence {
  CONTROLS = 'controls',
  CONTROL_CHECKS = 'control_checks',
}
